import {
	type CollectionFieldAnchor,
	type CollectionFieldAnchorProviderPointToPoint,
	type CollectionFieldData,
	type CollectionFieldDate,
	type CollectionFieldFileTool,
	type CollectionFieldHeight,
	type CollectionFieldImage,
	type CollectionFieldLine,
	type CollectionFieldLocation,
	type CollectionFieldNumber,
	type CollectionFieldOrientation,
	type CollectionFieldSelectList,
	type CollectionFieldSubForm,
	type CollectionFieldText,
	type CollectionFieldTrueSize,
	type CollectionFieldVector,
	type CollectionFieldVerticalSeparation,
	type CollectionFieldYesNo,
	type CollectionJSONObject,
	type FreeCropParameterState,
	type GammaBrightnessCorrectionState,
	type HiddenImageVisibilityState,
	IkeFieldType,
	type LabelDrawableState,
	type LineDrawableState,
	type MarkerDrawableState,
	MarkerType,
	type MaskDrawableState,
	ParseJSON,
	type VerticalSeparationDrawableState,
	type LocationData,
} from '@ikegps/core-api';
import type { Log } from 'bblog';
import type { CollectionModelCropParameterState } from '../cropParameter/cropParameter.state';
import { CollectionModelDrawableLinePoint } from '../drawable/line.point.drawable';
import { CollectionModelDrawableMarkerOffset } from '../drawable/marker.offset.drawable';
import { CollectionModelDrawableMask } from '../drawable/mask.drawable';
import { CollectionModelDrawableVerticalSeparation } from '../drawable/vertical.separation.drawable';
import type { CollectionModelHiddenImageVisibility } from '../imageVisibility/hidden.imageVisibility';
import type { CollectionModelImageVisibilityState } from '../imageVisibility/imageVisibility.state';
import { IkeId } from '../../../id';
import { IkeFetchableRaceError } from '../../../rest/fetchable';
import type { FormModel } from '../../form/form';
import type { FormModelField } from '../../form/form.field';
import { FormModelFieldSubForm } from '../../form/subform/form.subform';
import type { CollectionModelBrightnessCorrectionState } from '../brightnessCorrection/brightnessCorrection.state';
import type { CollectionModelGammaBrightnessCorrection } from '../brightnessCorrection/gamma.brightnessCorrection';
import type { CollectionModelBase } from '../collection.base';
import { CollectionModel } from '../collection.data';
import type { IkeCollectionLoader } from '../collection.loader';
import type { CollectionModelFreeCropParameter } from '../cropParameter/free.cropParameter';
import type { CollectionModelDrawableState } from '../drawable/drawable.state';
import { CollectionModelDrawableLabel } from '../drawable/label.drawable';
import { CollectionModelDrawableLine } from '../drawable/line.drawable';
import { CollectionModelDrawableMarker } from '../drawable/marker.drawable';
import type { CollectionModelFieldAnchor } from '../fields/anchor.field';
import type { CollectionModelFieldDate } from '../fields/date.field';
import type { CollectionModelFieldFileTool } from '../fields/file.tool.field';
import type { CollectionModelFieldSubForm } from '../fields/form.field';
import type { CollectionModelFieldHeight } from '../fields/height.field';
import { CollectionModelFieldImage } from '../fields/image.field';
import type { CollectionModelFieldLine } from '../fields/line.field';
import type { CollectionModelFieldLocation } from '../fields/location.field';
import type { CollectionModelFieldNestedList } from '../fields/nested.list.field';
import { CollectionModelFieldNumber } from '../fields/number.field';
import { CollectionModelFieldOrientation } from '../fields/orientation.field';
import type { CollectionModelFieldSelectList } from '../fields/select.list.field';
import type { CollectionModelFieldText } from '../fields/text.field';
import { CollectionModelFieldTrueSize } from '../fields/true.size.field';
import type { CollectionModelFieldVector } from '../fields/vector.field';
import type { CollectionModelFieldVerticalSeparation } from '../fields/vertical.separation.field';
import type { CollectionModelFieldYesNo } from '../fields/yes.no.field';
import {
	isAnchorLocationGPS,
	isAnchorManual,
	isAnchorP2P,
	isFreeCropParameter,
	isGammaBrightnessCorrection,
	isHeightPhotomeasureManualProvider,
	isHeightPhotomeasureProvider,
	isHeightProviderManual,
	isHeightSpanHeightProvider,
	isHiddenImageVisibility,
	isLineDrawable,
	isMarkerDrawable,
	isMaskDrawable,
	isVectorCollectionProvider,
	isVectorPointToPointProvider,
	isVectorProviderManual,
	isVerticalSeparationDrawable,
} from './collection.model.util';

export class IkeModelCollectionReader {
	collection: CollectionModel;
	json: CollectionJSONObject;
	schema: FormModel;
	loader: IkeCollectionLoader;
	log: Log | null = null;

	constructor(collection: CollectionJSONObject, schema: FormModel, loader: IkeCollectionLoader) {
		this.json = collection;
		this.schema = schema;
		this.loader = loader;
		this.collection = new CollectionModel(schema, loader);
	}

	read(): CollectionModel {
		const collectionJson = this.json;

		if (collectionJson.data != null && collectionJson.data.data != null) {
			this.collection.state.instanceId = collectionJson.data.data['_id'];
		}

		if (this.collection.instanceId == null) {
			this.collection.state.instanceId = collectionJson.objectId;
		}

		const state = this.collection.state;

		state.id = collectionJson.objectId;
		state.uuid = collectionJson.uuid;
		state.createdAt = new Date(collectionJson.createdAt);
		state.updatedAt = new Date(collectionJson.updatedAt);
		state.collectedBy = collectionJson.collectedBy;
		state.collectedWith = collectionJson.collectedWith;
		state.assignedTo = collectionJson.assignedTo;
		state.updatedBy = collectionJson.updatedBy;
		state.revision = collectionJson.revision;
		state.jobId = collectionJson.jobuuid;
		state.variantType = collectionJson.variantType;
		state.variantTypeName = collectionJson.variantTypeName;
		state.variantForUuid = collectionJson.variantForUuid;
		state.variants = collectionJson.variants;

		if (collectionJson.collectedAt != null) {
			state.collectedAt = ParseJSON.fromDate(collectionJson.collectedAt);
		}

		this.readMeasurements();
		this.readBrightnessCorrections();
		this.readCropParameters();
		this.readImageVisibilities();

		for (const field of this.schema.fields) {
			this.readField(this.collection, field, collectionJson.data.data);
		}

		return this.collection;
	}

	readField(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldData): void {
		const value = source[schema.id];

		// Check to make sure the value actually has something interesting in it.
		if (value == null) {
			return;
		}

		// Process sub forms
		if (FormModelFieldSubForm.isSubForm(schema)) {
			return this.loadSubForm(target, schema, <CollectionFieldSubForm>value);
		}

		// Text based fields can just be set
		if (schema.type === IkeFieldType.TEXT) {
			const f = target.getField<CollectionModelFieldText>(schema.id);
			f.Mutator.manual(<CollectionFieldText>value);
			return;
		}

		if (schema.type === IkeFieldType.FILE_TOOL) {
			return this.loadFileTool(target, schema, <CollectionFieldFileTool>value);
		}

		if (schema.type === IkeFieldType.DATE) {
			const f = target.getField<CollectionModelFieldDate>(schema.id);
			f.Mutator.manual(<CollectionFieldDate>value);
			return;
		}

		if (schema.type === IkeFieldType.YES_NO) {
			const f = target.getField<CollectionModelFieldYesNo>(schema.id);
			f.Mutator.manual(<CollectionFieldYesNo>value);
			return;
		}

		if (schema.type === IkeFieldType.SELECT_LIST) {
			const f = target.getField<CollectionModelFieldSelectList>(schema.id);
			f.Mutator.manual(<CollectionFieldSelectList>value);
			return;
		}

		if (schema.type === IkeFieldType.NUMBER) {
			return this.loadNumber(target, schema, <CollectionFieldNumber>value);
		}

		if (schema.type === IkeFieldType.ORIENTATION) {
			return this.loadOrientation(target, schema, <CollectionFieldOrientation>value);
		}

		// Load in CaptureFile information
		if (schema.type === IkeFieldType.TRUE_SIZE) {
			return this.loadCaptures(target, schema, <CollectionFieldTrueSize>value);
		}

		if (schema.type === IkeFieldType.IMAGE) {
			return this.loadCaptures(target, schema, <CollectionFieldImage>value);
		}

		if (schema.type === IkeFieldType.VECTOR) {
			return this.loadVector(target, schema, <CollectionFieldVector>value);
		}

		if (schema.type === IkeFieldType.ANCHOR) {
			return this.loadAnchor(target, schema, <CollectionFieldAnchor>value);
		}

		if (schema.type === IkeFieldType.LOCATION) {
			return this.loadLocation(target, schema, <CollectionFieldLocation>value);
		}

		if (schema.type === IkeFieldType.NESTED_LIST) {
			const nlField = target.getField<CollectionModelFieldNestedList>(schema.id);
			nlField.Mutator.manual((value as string[][])[0]);
			return;
		}

		if (schema.type === IkeFieldType.HEIGHT) {
			return this.loadHeight(target, schema, <CollectionFieldHeight>value);
		}

		if (schema.type === IkeFieldType.VERTICAL_SEPARATION) {
			return this.loadVerticalSeparation(target, schema, <CollectionFieldVerticalSeparation>value);
		}

		if (schema.type === IkeFieldType.LINE) {
			return this.loadLine(target, schema, <CollectionFieldLine>value);
		}

		throw new Error(`Unable to find field Serializer for ${schema.type}`);
	}

	loadSubForm(target: CollectionModelBase, schema: FormModelFieldSubForm, source: CollectionFieldSubForm) {
		const instances: CollectionFieldSubForm = source;
		const field = target.getField<CollectionModelFieldSubForm>(schema.id);

		if (instances.length === 0) {
			field.Mutator.clear();
		}

		for (const data of instances) {
			const instanceId = data._id || IkeId.generate();
			field.Mutator.create(instanceId);
			const instance = field.getInstance(instanceId);

			for (const f of schema.fields) {
				this.readField(instance, f, data);
			}
		}
	}

	loadCaptures(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldTrueSize) {
		const field = target.getField(schema.id);
		if (source.length === 0) {
			if (CollectionModelFieldTrueSize.isTrueSizeField(field)) {
				field.Mutator.clear();
			} else if (CollectionModelFieldImage.isImageField(field)) {
				field.Mutator.clear();
			}

			return;
		}

		for (const ts of source) {
			if (CollectionModelFieldTrueSize.isTrueSizeField(field)) {
				field.Mutator.add(ts.capture.objectId);
			} else if (CollectionModelFieldImage.isImageField(field)) {
				field.Mutator.add(ts.capture.objectId);
			}
		}
	}

	loadLocation(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldLocation) {
		const field = target.getField<CollectionModelFieldLocation>(schema.id);
		const isLocationData = (x: CollectionFieldLocation): x is LocationData =>
			(x as { provider?: unknown }).provider == null;
		if (isLocationData(source)) {
			// We are unsure if this is actually a GPS reading or a manual reading.
			// In theory manual readings will not have accuracy

			if (source.accuracy != null) {
				field.Mutator.gps(source, null);
				return;
			}

			field.Mutator.manual(source);
			return;
		}

		if (source.provider === 'edited') {
			field.Mutator.edited(source.value);
			return;
		}

		if (source.provider === 'gps') {
			field.Mutator.gps(source.value, source);
			return;
		}

		if (source.provider === 'manual') {
			field.Mutator.manual(source.value);
			return;
		}

		field.Mutator.capture(source.capture.objectId || source.capture.uuid, source.datum);
	}

	loadLine(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldLine) {
		const field = target.getField<CollectionModelFieldLine>(schema.id);

		if (source.provider === 'field_manual') {
			field.Mutator.fieldManual(source.value, source.antennaHeight);
			return;
		}

		if (source.provider === 'field_continuous') {
			field.Mutator.fieldContinuous(source.value, source.continuousSettings, source.antennaHeight);
			return;
		}

		field.Mutator.office(source.value);
	}

	loadNumber(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldNumber) {
		const field = <CollectionModelFieldNumber>target.getField(schema.id);

		if (typeof source === 'number') {
			field.Mutator.manual(source);
			return;
		}

		if (typeof source === 'string') {
			field.Mutator.manual(parseFloat(source));
			return;
		}

		if (source.provider === 'manual') {
			field.Mutator.manual(source.value);
			return;
		}

		if (source.provider === 'bearing' || source.provider === 'backbearing') {
			if (source.capture == null) {
				// Some bad data doesn't have a "capture". Revert to manual.
				field.Mutator.manual(source.value);
				return;
			}

			if (source.provider === CollectionModelFieldNumber.PROVIDER_BEARING) {
				field.Mutator.bearing(source.capture.objectId || source.capture.uuid);
				return;
			}

			if (source.provider === CollectionModelFieldNumber.PROVIDER_BACK_BEARING) {
				field.Mutator.backBearing(source.capture.objectId || source.capture.uuid);
				return;
			}
		}

		if (source.provider === 'sag') {
			const points: string[] = source.points.map((p) => p.objectId || p.uuid);
			field.Mutator.sag(source.start.objectId || source.start.uuid, source.end.objectId || source.end.uuid, points);
			return;
		}

		throw new Error(`Unable to find field Serializer for ${schema.type} with provider ${source['provider']}`);
	}

	loadOrientation(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldOrientation) {
		const field = <CollectionModelFieldOrientation>target.getField(schema.id);

		if (typeof source === 'number') {
			field.Mutator.manual(source);
			return;
		}

		if (typeof source === 'string') {
			field.Mutator.manual(parseFloat(source));
			return;
		}

		if (source.provider === 'manual') {
			field.Mutator.manual(source.value);
			return;
		}

		if (source.provider === 'bearing' || source.provider === 'backbearing') {
			if (source.capture == null) {
				// Some bad data doesn't have a "capture". Revert to manual.
				field.Mutator.manual(source.value);
				return;
			}

			if (source.provider === CollectionModelFieldOrientation.PROVIDER_BEARING) {
				field.Mutator.bearing(source.capture.objectId || source.capture.uuid);
				return;
			}

			if (source.provider === CollectionModelFieldOrientation.PROVIDER_BACK_BEARING) {
				field.Mutator.backBearing(source.capture.objectId || source.capture.uuid);
				return;
			}
		}

		// Legacy Number-field data kept its place when the form field was switched to Orientation.
		// Sag has no equivalent here — leave the target blank; the upgrade flow handles the drop.
		if (source['provider'] === 'sag') {
			return;
		}

		throw new Error(`Unable to find field Serializer for ${schema.type} with provider ${source['provider']}`);
	}

	loadVector(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldVector) {
		const field = <CollectionModelFieldVector>target.getField(schema.id);

		if (isVectorPointToPointProvider(source)) {
			const cc = source.capture.points.map((ts) => ts.capture.objectId);
			field.Mutator.p2p(cc);
			return;
		}

		if (isVectorCollectionProvider(source)) {
			field.Mutator.collection(source.collectionId, source.value);
			return;
		}

		if (isVectorProviderManual(source)) {
			field.Mutator.manual(source.value);
			return;
		}

		return;
	}

	loadAnchor(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldAnchor) {
		const field = <CollectionModelFieldAnchor>target.getField(schema.id);

		if (isAnchorLocationGPS(source)) {
			field.Mutator.gps(source.value);
			if (source.antennaHeight != null) {
				field.Mutator.setAntennaHeight(source.antennaHeight);
			}
			return;
		}

		if (isAnchorP2P(source)) {
			const typedSource = source as unknown as CollectionFieldAnchorProviderPointToPoint;
			const cc = typedSource.capture.points.map((ts) => ts.capture.objectId);
			field.Mutator.p2p(cc, typedSource.value.placement);
			return;
		}

		if (isAnchorManual(source)) {
			field.Mutator.manualAnchor(source.value);
			return;
		}

		return;
	}

	loadHeight(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldHeight) {
		const field = <CollectionModelFieldHeight>target.getField(schema.id);
		if (isHeightProviderManual(source)) {
			field.Mutator.manual(source.value);
			return;
		}

		if (isHeightSpanHeightProvider(source)) {
			const cc = source.capture.points.map((ts) => ts.capture.objectId);
			field.Mutator.spanHeight(cc);
			return;
		}

		if (isHeightPhotomeasureProvider(source) || isHeightPhotomeasureManualProvider(source)) {
			for (const captureId of Array.from(this.collection.state.drawableStates.keys())) {
				const drawableState = this.collection.getDrawableState(captureId);
				if (!drawableState.hasDrawable(source.drawable)) {
					continue;
				}

				const drawable = drawableState.getDrawable<CollectionModelDrawableMarker>(source.drawable);
				if (drawable == null) {
					field.Mutator.manual(null); // TODO should we error here?
					return;
				}

				field.Mutator.drawable(drawable);
				return;
			}

			// Unable to find drawable
			field.Mutator.manual(source.value); // TODO should we error here?
			return;
		}

		throw new Error(`Field ${schema.id}: Unable to parse Provider`);
	}

	loadVerticalSeparation(
		target: CollectionModelBase,
		schema: FormModelField,
		source: CollectionFieldVerticalSeparation
	) {
		const field = <CollectionModelFieldVerticalSeparation>target.getField(schema.id);

		if (!source.drawableTop || !source.drawableBottom) {
			field.Mutator.manual(source.value); // TODO should we error here?
			return;
		}

		for (const captureId of Array.from(this.collection.state.drawableStates.keys())) {
			const drawableState = this.collection.getDrawableState(captureId);
			// if one of the pairs is missing, continue
			if (!drawableState.hasDrawable(source.drawableTop) || !drawableState.hasDrawable(source.drawableBottom)) {
				continue;
			}

			const drawableTop = drawableState.getDrawable<CollectionModelDrawableMarker>(source.drawableTop);
			if (drawableTop == null) {
				this.log?.error(
					{ source, captureId, schema, drawableState },
					"Expected to find drawableTop in drawableState but it wasn't found, setting field to null"
				);
				field.Mutator.manual(null);
				return;
			}

			const drawableBottom = drawableState.getDrawable<CollectionModelDrawableMarker>(source.drawableBottom);

			try {
				// If a vertical separation drawable was not persisted, create one.
				// We do this because vertical separation drawables are a more recent addition
				// and not all collections have them in their persisted state.
				const drawableVertSep = source.drawable
					? drawableState.getDrawable<CollectionModelDrawableVerticalSeparation>(source.drawable)
					: CollectionModelDrawableVerticalSeparation.createWithLabel(
							drawableState,
							drawableTop.id,
							drawableBottom.id,
							field.id
						);

				field.Mutator.setDrawables(drawableTop, drawableBottom, drawableVertSep);

				// Link marker labels after the state is set so they don't call field.Mutator.drawable().
				// This prevents an issue where the captureJSON is not loaded yet at load time.

				drawableTop.label.linkVerticalSeparation(field);
				drawableBottom.label.linkVerticalSeparation(field);
				this.log?.debug({ fieldId: field.id }, 'Loaded Vertical Separation field');
			} catch (error: unknown) {
				if (error instanceof IkeFetchableRaceError) {
					this.log?.warn({ error, source }, 'Caught IkeFetchableRaceError, skipping Vertical Separation Field');
					return;
				}
				throw error;
			}

			return;
		}

		// Unable to find drawable
		field.Mutator.manual(source.value); // TODO should we error here?
	}

	loadFileTool(target: CollectionModelBase, schema: FormModelField, source: CollectionFieldFileTool) {
		const field = <CollectionModelFieldFileTool>target.getField(schema.id);

		field.Mutator.manual({
			filename: source.filename,
			attachmentId: source.attachmentId,
			size: source.size,
			metadata: source.metadata,
		});
	}

	readMeasurements() {
		const collectionJSON = this.json;
		if (collectionJSON.measurements == null) {
			return;
		}
		const measurements = Object.keys(collectionJSON.measurements);
		if (measurements.length === 0) {
			return;
		}
		for (const measurementId of measurements) {
			const measurement = collectionJSON.measurements[measurementId];
			if (measurement == null) {
				continue;
			}

			if (measurement.state.length === 0) {
				continue;
			}

			if (measurement.captureId == null) {
				throw new Error('Unable to read measurement, missing captureId ' + measurementId);
			}

			const drawableState = this.collection.getDrawableState(measurement.captureId);
			for (const drawable of measurement.state) {
				if (drawableState.drawables.has(drawable.id)) {
					continue;
				}

				if (isMarkerDrawable(drawable)) {
					this.createMarkerDrawable(drawableState, drawable);
				} else if (isLineDrawable(drawable)) {
					this.createLineDrawable(drawableState, drawable);
				} else if (isMaskDrawable(drawable)) {
					this.createMaskDrawable(drawableState, drawable);
				} else if (isVerticalSeparationDrawable(drawable)) {
					this.createVerticalSeparationDrawable(drawableState, drawable);
				} else {
					throw new Error('Unable to read drawable, unknown type ' + drawable.tag);
				}
			}
		}
	}

	createMarkerDrawable(drawableState: CollectionModelDrawableState, obj: MarkerDrawableState) {
		if (!obj.label) {
			throw new Error(`Marker drawable requires a label state but one was not provided`);
		}

		this.createLabelDrawable(drawableState, obj.label, obj.id);

		switch (obj.tag) {
			case 'marker':
				// NOTE: Currently Base Offset markers can be serialized with "tag" set to "marker"
				// but they are not Markers -- they are Marker Offsets -- so we check "tag" and "type".
				switch (obj.type) {
					case MarkerType.NONE:
						return new CollectionModelDrawableMarker(
							drawableState,
							obj.id,
							obj.y,
							obj.label?.id,
							obj.type,
							obj.insight,
							obj.pair
						);
					case MarkerType.BASE:
						return new CollectionModelDrawableMarker(
							drawableState,
							obj.id,
							obj.y,
							obj.label?.id,
							obj.type,
							obj.insight,
							obj.pair,
							obj.offset
						);
					case MarkerType.BASE_OFFSET:
						return new CollectionModelDrawableMarkerOffset(drawableState, obj.id, obj.label.id);
					default:
						throw new Error(`Unrecognized marker type: ${obj['type']}`);
				}
			case 'offset':
				return new CollectionModelDrawableMarkerOffset(drawableState, obj.id, obj.label.id);
			default:
				throw new Error(`Unrecognized marker tag: ${obj.tag}`);
		}
	}

	createLabelDrawable(drawableState: CollectionModelDrawableState, obj: LabelDrawableState, parentId: string) {
		return new CollectionModelDrawableLabel(
			drawableState,
			obj.id,
			obj.x,
			obj.y,
			parentId,
			typeof obj.text === 'string' ? [obj.text] : obj.text,
			undefined
		);
	}

	createLineDrawable(drawableState: CollectionModelDrawableState, obj: LineDrawableState) {
		this.createLabelDrawable(drawableState, obj.label, obj.id);

		const start = new CollectionModelDrawableLinePoint(
			drawableState,
			`${obj.id}:start`,
			obj.start.x,
			obj.start.y,
			obj.id
		);
		const end = new CollectionModelDrawableLinePoint(drawableState, `${obj.id}:end`, obj.end.x, obj.end.y, obj.id);
		return new CollectionModelDrawableLine(drawableState, obj.id, obj.label.id, start.id, end.id);
	}

	createMaskDrawable(drawableState: CollectionModelDrawableState, obj: MaskDrawableState) {
		if (!obj.pukeko_data) {
			throw new Error(`Failed to deserialize Mask Drawable "${obj.id}": Pukeko data is missing`);
		}
		return new CollectionModelDrawableMask(drawableState, obj.id, obj.pukeko_data);
	}

	createVerticalSeparationDrawable(drawableState: CollectionModelDrawableState, obj: VerticalSeparationDrawableState) {
		// Detect malformed data (primarily during development)
		if (!obj.topMarkerId || !obj.bottomMarkerId || !obj.fieldId || !obj.label || !obj.id) {
			throw new Error(
				`Malformed vertical separation drawable state encountered during JSON deserialization (collection ${this.collection.id}) (capture ${drawableState.captureId}): ${JSON.stringify(obj)}`
			);
		}
		const label = this.createLabelDrawable(drawableState, obj.label, obj.id);
		return new CollectionModelDrawableVerticalSeparation(
			drawableState,
			obj.topMarkerId,
			obj.bottomMarkerId,
			obj.fieldId,
			label,
			obj.id
		);
	}

	readBrightnessCorrections() {
		const collectionJSON = this.json;
		if (collectionJSON.brightnessCorrections == null) {
			return;
		}
		const brightnessCorrections = Object.keys(collectionJSON.brightnessCorrections);
		if (brightnessCorrections.length === 0) {
			return;
		}
		for (const brightnessCorrectionId of brightnessCorrections) {
			const brightnessCorrection = collectionJSON.brightnessCorrections[brightnessCorrectionId];
			if (brightnessCorrection == null) {
				continue;
			}

			if (brightnessCorrection.state.length === 0) {
				continue;
			}

			if (brightnessCorrection.captureId == null) {
				throw new Error('Unable to read brightness correction, missing captureId ' + brightnessCorrectionId);
			}

			const brightnessCorrectionState = this.collection.getBrightnessCorrectionState(brightnessCorrection.captureId);
			for (const drawableBrightnessCorrection of brightnessCorrection.state) {
				if (brightnessCorrectionState.brightnessCorrections[drawableBrightnessCorrection.id]) {
					continue;
				}

				if (isGammaBrightnessCorrection(drawableBrightnessCorrection)) {
					this.createGammaBrightnessCorrection(brightnessCorrectionState, drawableBrightnessCorrection);
				} else {
					continue; // TODO should we error here?
				}
			}
		}
	}

	readImageVisibilities() {
		const collectionJSON = this.json;
		if (collectionJSON.imageVisibilities == null) {
			return;
		}
		const imageVisibilities = Object.keys(collectionJSON.imageVisibilities);
		if (imageVisibilities.length === 0) {
			return;
		}
		for (const imageVisibilityId of imageVisibilities) {
			const imageVisibility = collectionJSON.imageVisibilities[imageVisibilityId];
			if (imageVisibility == null || !imageVisibility.state) {
				continue;
			}

			if (imageVisibility.captureId == null) {
				throw new Error('Unable to read image visibility, missing captureId ' + imageVisibilityId);
			}

			const imageVisibilityState = this.collection.getImageVisibilityState(imageVisibility.captureId);

			if (imageVisibilityState.imageVisibility) {
				continue;
			}

			if (isHiddenImageVisibility(imageVisibility.state)) {
				this.createHiddenImageVisibility(imageVisibilityState, imageVisibility.state);
			}
		}
	}

	createGammaBrightnessCorrection(
		brightnessCorrectionState: CollectionModelBrightnessCorrectionState,
		obj: GammaBrightnessCorrectionState
	) {
		const gammaId = brightnessCorrectionState.Mutator.create('gamma', obj.id);
		const gamma = brightnessCorrectionState.getBrightnessCorrection<CollectionModelGammaBrightnessCorrection>(gammaId);
		const gammaState = gamma.state;
		gammaState.exponent = obj.exponent;
		return gamma;
	}

	createHiddenImageVisibility(
		hiddenImageVisibilityState: CollectionModelImageVisibilityState,
		obj: HiddenImageVisibilityState
	) {
		const hidden = hiddenImageVisibilityState.getImageVisibility<CollectionModelHiddenImageVisibility>();
		const hiddenState = hidden.state;
		hiddenState.hidden = obj.hidden;
		return hidden;
	}

	readCropParameters() {
		const collectionJSON = this.json;
		if (collectionJSON.cropParameters == null) {
			return;
		}
		const cropParameters = Object.keys(collectionJSON.cropParameters);
		if (cropParameters.length === 0) {
			return;
		}
		for (const cropParameterId of cropParameters) {
			const cropParameter = collectionJSON.cropParameters[cropParameterId];
			if (cropParameter == null) {
				continue;
			}

			if (cropParameter.state.length === 0) {
				continue;
			}

			if (cropParameter.captureId == null) {
				throw new Error('Unable to read crop parameter, missing captureId ' + cropParameterId);
			}

			const cropParameterState = this.collection.getCropParameterState(cropParameter.captureId);
			for (const drawableCropParameter of cropParameter.state) {
				if (cropParameterState.cropParameters[drawableCropParameter.id]) {
					continue;
				}

				if (isFreeCropParameter(drawableCropParameter)) {
					this.createFreeCropParameter(cropParameterState, drawableCropParameter);
				} else {
					continue; // TODO should we error here?
				}
			}
		}
	}

	createFreeCropParameter(cropParameterState: CollectionModelCropParameterState, obj: FreeCropParameterState) {
		const freeId = cropParameterState.Mutator.create('free', obj.id);
		const free = cropParameterState.getCropParameter<CollectionModelFreeCropParameter>(freeId);
		const freeState = free.state;
		freeState.x = obj.x;
		freeState.y = obj.y;
		freeState.width = obj.width;
		freeState.height = obj.height;
		return free;
	}
}
